import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaExclamationTriangle } from "react-icons/fa";
import { useAccess } from "@/utils/access-provider";
import { onSessionExpired, resetSessionExpired } from "@/utils/session-expired";
import useTranslation from "@/hooks/use-translation";
import { Button } from "@/components/ui";
import { Typography } from "@/components/ui/typography";
import { Z_INDEX, zIndexStyle } from "@/utils/z-index";

/**
 * Non-dismissable modal shown when the refresh token has expired.
 * Prompts the user to re-login instead of silently failing requests.
 */
const SessionExpiredModal: React.FC = () => {
  const [open, setOpen] = useState(false);
  const { logout } = useAccess();
  const { t } = useTranslation();

  useEffect(() => {
    return onSessionExpired(() => setOpen(true));
  }, []);

  const handleLogin = () => {
    resetSessionExpired();
    setOpen(false);
    logout();
  };

  const title = t("session_expired_title") === "session_expired_title"
    ? "Session Expired"
    : t("session_expired_title");

  const subtitle = t("session_expired_subtitle") === "session_expired_subtitle"
    ? "Your session has expired. Please log in again to continue."
    : t("session_expired_subtitle");

  const description = t("session_expired_description") === "session_expired_description"
    ? "For security reasons, your session has expired. Any unsaved changes may be lost."
    : t("session_expired_description");

  const loginLabel = t("login") === "login" ? "Log in" : t("login");

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop - no click to close */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-md"
            style={zIndexStyle(Z_INDEX.MODAL)}
            aria-hidden="true"
          />

          {/* Modal container */}
          <div
            className="fixed inset-0 flex items-center justify-center p-4"
            style={zIndexStyle(Z_INDEX.MODAL + 1)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 30 }}
              transition={{
                type: "spring",
                damping: 25,
                stiffness: 300,
              }}
              className="w-full max-w-md bg-surface-tertiary rounded-arc-2xl shadow-arc-2xl overflow-hidden"
            >
              {/* Header with icon */}
              <div className="px-8 pt-8 pb-4 text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{
                    type: "spring",
                    delay: 0.1,
                    damping: 15,
                    stiffness: 200,
                  }}
                  className="inline-flex items-center justify-center w-16 h-16 mb-4 rounded-arc-xl bg-danger/10"
                >
                  <FaExclamationTriangle className="text-3xl text-danger" />
                </motion.div>

                <Typography variant="h4" className="font-semibold text-content-primary mb-2">
                  {title}
                </Typography>

                <Typography variant="text-sm" className="text-content-secondary">
                  {subtitle}
                </Typography>
              </div>

              {/* Body */}
              <div className="px-8 pb-8">
                <div className="mb-6 p-4 bg-surface-secondary rounded-arc-lg">
                  <Typography variant="text-sm" className="text-content-tertiary">
                    {description}
                  </Typography>
                </div>

                {/* Login button */}
                <Button
                  variant="primary"
                  size="lg"
                  fullWidth
                  onClick={handleLogin}
                >
                  {loginLabel}
                </Button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

SessionExpiredModal.displayName = "SessionExpiredModal";

export { SessionExpiredModal };
